import type {
  IdentityContext,
  SaasLead,
  SaasLeadListInput,
  SaasLeadRepository,
  SubmitSaasLeadInput,
  UpdateSaasLeadInput,
} from '@zetyar/data-contracts'
import type { UUID } from '@zetyar/domain'
import type { SupabaseClient } from '@supabase/supabase-js'

interface LeadRow {
  readonly id: string
  readonly full_name: string
  readonly email: string
  readonly phone: string | null
  readonly organization_name: string
  readonly organization_type: SaasLead['organizationType']
  readonly members_estimate: SaasLead['membersEstimate']
  readonly message: string | null
  readonly consent_at: string
  readonly source: string
  readonly status: SaasLead['status']
  readonly notes: string | null
  readonly handled_by_person_id: string | null
  readonly created_at: string
  readonly updated_at: string
}

const LEAD_SELECT =
  'id, full_name, email, phone, organization_name, organization_type, members_estimate, message, consent_at, source, status, notes, handled_by_person_id, created_at, updated_at'

function mapLead(row: LeadRow): SaasLead {
  return {
    id: row.id,
    fullName: row.full_name,
    email: row.email,
    phone: row.phone,
    organizationName: row.organization_name,
    organizationType: row.organization_type,
    membersEstimate: row.members_estimate,
    message: row.message,
    consentAt: row.consent_at,
    source: row.source,
    status: row.status,
    notes: row.notes,
    handledByPersonId: row.handled_by_person_id,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
}

export function createSupabaseSaasLeadRepository(client: SupabaseClient): SaasLeadRepository {
  return {
    // Anonymous: the RPC validates, rate-limits and inserts (0034_saas_leads.sql). The table itself is
    // never readable or writable by anon.
    async submit(input: SubmitSaasLeadInput) {
      const { error } = await client.rpc('submit_saas_lead', {
        p_full_name: input.fullName,
        p_email: input.email,
        p_phone: input.phone ?? null,
        p_organization_name: input.organizationName,
        p_organization_type: input.organizationType,
        p_members_estimate: input.membersEstimate,
        p_message: input.message ?? null,
        p_consent: input.consent,
        p_website: input.website ?? null,
      })
      if (error) throw error
    },

    async listForPlatform(_context: IdentityContext, { status, limit }: SaasLeadListInput) {
      let query = client.from('saas_leads').select(LEAD_SELECT)
      if (status) query = query.eq('status', status)

      const { data, error } = await query
        .order('created_at', { ascending: false })
        .limit(Math.max(1, Math.min(limit, 200)))

      if (error) throw error
      return (data as unknown as LeadRow[]).map(mapLead)
    },

    async countNew(_context: IdentityContext) {
      const { count, error } = await client
        .from('saas_leads')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'new')

      if (error) throw error
      return count ?? 0
    },

    async update(context: IdentityContext, id: UUID, input: UpdateSaasLeadInput) {
      const { error } = await client
        .from('saas_leads')
        .update({
          status: input.status,
          notes: input.notes?.trim() || null,
          handled_by_person_id: context.personId,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
      if (error) throw error
    },
  }
}
